import React, { useEffect, useState } from 'react'
import { Form, Alert, Row, Col } from 'react-bootstrap'
import TreeNode from './TreeNode'
import { defaultRecipe, validateRecipe } from './recipe-helpers'

// `hideMetadata` skips the name/template/description fields — used by
// surfaces that render their own metadata inputs (e.g. ComposeAnalyticModal)
// and only want the predicate tree editor. `hideJson` removes the raw-JSON
// toggle — user-facing views never expose recipe JSON; it stays admin-only.
const RecipeBuilder = ({ value, registry, onChange, validationErrors = [], hideMetadata = false, hideJson = false }) => {
  const [showJson, setShowJson] = useState(false)

  useEffect(() => {
    if (!value?.recipe) {
      onChange({
        ...(value || {}),
        recipe: defaultRecipe()
      })
    }
  }, [value, onChange])

  if (!value?.recipe) return null

  const setName = (name) => onChange({ ...value, name })
  const setDescTemplate = (description_template) => onChange({ ...value, description_template })
  const setDescription = (description) => onChange({ ...value, description })
  const setRecipe = (recipe) => onChange({ ...value, recipe })

  const inlineErrors = validationErrors.length
    ? validationErrors
    : validateRecipe(value.recipe, registry?.predicates)

  return (
    <div>
      {!hideMetadata && (
        <>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small text-muted">Name</Form.Label>
                <Form.Control
                  type="text"
                  value={value.name || ''}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Weekend purchases over $500"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small text-muted">Description template</Form.Label>
                <Form.Control
                  type="text"
                  value={value.description_template || ''}
                  onChange={(e) => setDescTemplate(e.target.value)}
                  placeholder="{column_name} renders the row's value at evaluation time"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="small text-muted">Description (admin-facing)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={value.description || ''}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Internal notes about this analytic. Shown alongside the rule definition."
                />
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Predicate tree</h6>
        {!hideJson && (
          <Form.Check
            type="switch"
            id="show-json"
            label="Show JSON"
            checked={showJson}
            onChange={(e) => setShowJson(e.target.checked)}
          />
        )}
      </div>

      {inlineErrors.length > 0 && (
        <Alert variant="warning" className="py-2 small">
          {inlineErrors.map((err, idx) => (
            <div key={idx}>
              <code>{err.path}</code> — {err.message}
            </div>
          ))}
        </Alert>
      )}

      <TreeNode
        node={value.recipe}
        registry={registry}
        onChange={setRecipe}
        onRemove={() => setRecipe(defaultRecipe())}
        removable={false}
      />

      {showJson && !hideJson && (
        <pre className="bg-light p-3 mt-3 small" style={{ maxHeight: 300, overflow: 'auto' }}>
          {JSON.stringify(value.recipe, null, 2)}
        </pre>
      )}
    </div>
  )
}

export default RecipeBuilder
